<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { panelKey } from 'shared/helpers/copilotMentionContext';
import CopilotConversationPanel from './CopilotConversationPanel.vue';
import CopilotEventPanel from './CopilotEventPanel.vue';
import CopilotContactPanel from './CopilotContactPanel.vue';
import CopilotArticlePanel from './CopilotArticlePanel.vue';
import CopilotPricingPlanPanel from './CopilotPricingPlanPanel.vue';
import CopilotQuotePanel from './CopilotQuotePanel.vue';
import CopilotBookingWidgetPanel from './CopilotBookingWidgetPanel.vue';
import CopilotHtmlReportPanel from './CopilotHtmlReportPanel.vue';

const props = defineProps({
  panels: { type: Array, required: true },
  accountId: { type: [String, Number], required: true },
  activeIndex: { type: Number, default: 0 },
});

const emit = defineEmits(['close', 'close-all', 'update:activeIndex']);

const { t } = useI18n();
const store = useStore();

const activeIndex = computed({
  get: () => props.activeIndex,
  set: value => emit('update:activeIndex', value),
});

watch(
  () => props.panels.length,
  len => {
    if (activeIndex.value >= len) {
      activeIndex.value = Math.max(0, len - 1);
    }
  }
);

function eventPanelTitle(panel) {
  const event = store.getters['planningEvents/getRecords']?.find(
    record => String(record.id) === String(panel.id)
  );
  return event?.name || panel.title || t('COPILOT.PANEL.EVENT');
}

function panelLabel(panel) {
  if (panel.type === 'conversation')
    return `${t('COPILOT.PANEL.CONVERSATION')} #${panel.id}`;
  if (panel.type === 'contact')
    return `${t('COPILOT.PANEL.CONTACT')} #${panel.id}`;
  if (panel.type === 'article')
    return `${t('COPILOT.PANEL.ARTICLE')} #${panel.id}`;
  if (panel.type === 'pricing_plan')
    return `${t('COPILOT.PANEL.PRICING_PLAN')} #${panel.id}`;
  if (panel.type === 'quote') return t('COPILOT.PANEL.QUOTE');
  if (panel.type === 'booking_widget')
    return t('COPILOT.PANEL.BOOKING_WIDGET');
  if (panel.type === 'report')
    return panel.data?.title || t('COPILOT.REPORT.TITLE');
  if (panel.type === 'event') return eventPanelTitle(panel);
  return `${t('COPILOT.PANEL.EVENT')} #${panel.id}`;
}

function panelIcon(panel) {
  if (panel.type === 'conversation') return 'i-lucide-message-square';
  if (panel.type === 'contact') return 'i-lucide-user';
  if (panel.type === 'article') return 'i-lucide-book-open';
  if (panel.type === 'pricing_plan') return 'i-lucide-receipt';
  if (panel.type === 'quote') return 'i-lucide-file-text';
  if (panel.type === 'booking_widget') return 'i-lucide-calendar-check-2';
  if (panel.type === 'report') return 'i-lucide-file-text';
  return 'i-lucide-calendar';
}

function panelTabKey(panel) {
  return panelKey(panel);
}
</script>

<template>
  <div
    class="flex flex-col w-full min-w-0 h-full overflow-hidden bg-n-solid-1"
  >
    <div class="flex items-center border-b border-n-weak shrink-0 bg-n-solid-2">
      <div class="flex flex-1 overflow-x-auto">
        <button
          v-for="(panel, idx) in panels"
          :key="panelTabKey(panel)"
          class="group flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-r border-n-weak transition-colors shrink-0 relative"
          :class="
            idx === activeIndex
              ? 'bg-n-solid-1 text-n-slate-12'
              : 'bg-n-solid-2 text-n-slate-11 hover:bg-n-solid-1 hover:text-n-slate-12'
          "
          :title="panelLabel(panel)"
          @click="activeIndex = idx"
        >
          <span class="size-3.5" :class="panelIcon(panel)" />
          <span class="max-w-[120px] truncate">{{ panelLabel(panel) }}</span>
          <span
            class="i-lucide-x size-3 ml-1 opacity-0 group-hover:opacity-100 hover:text-n-ruby-11 transition-opacity"
            @click.stop="emit('close', idx)"
          />
          <span
            v-if="idx === activeIndex"
            class="absolute bottom-0 left-0 right-0 h-0.5 bg-n-brand"
          />
        </button>
      </div>
      <button
        class="flex items-center justify-center px-2.5 text-n-slate-9 hover:text-n-slate-12 transition-colors"
        :title="t('COPILOT.PANEL.CLOSE')"
        @click="emit('close-all')"
      >
        <span class="i-lucide-panel-right-close size-4" />
      </button>
    </div>

    <div class="flex-1 min-h-0 overflow-hidden">
      <template v-for="(panel, idx) in panels" :key="panelTabKey(panel)">
        <div v-show="idx === activeIndex" class="h-full">
          <CopilotConversationPanel
            v-if="panel.type === 'conversation'"
            :conversation-id="panel.id"
            :account-id="accountId"
          />
          <CopilotEventPanel
            v-else-if="panel.type === 'event'"
            :event-id="panel.id"
            :account-id="accountId"
          />
          <CopilotContactPanel
            v-else-if="panel.type === 'contact'"
            :contact-id="panel.id"
            :account-id="accountId"
          />
          <CopilotArticlePanel
            v-else-if="panel.type === 'article'"
            :article-id="panel.id"
            :portal-slug="panel.portalSlug"
            :account-id="accountId"
          />
          <CopilotPricingPlanPanel
            v-else-if="panel.type === 'pricing_plan'"
            :pricing-plan-id="panel.id"
            :account-id="accountId"
          />
          <CopilotQuotePanel
            v-else-if="panel.type === 'quote'"
            :quote-ref="panel.id"
            :account-id="accountId"
          />
          <CopilotBookingWidgetPanel
            v-else-if="panel.type === 'booking_widget'"
            :booking-widget-id="panel.id"
            :account-id="accountId"
          />
          <CopilotHtmlReportPanel
            v-else-if="panel.type === 'report'"
            :report="panel.data"
          />
        </div>
      </template>
    </div>
  </div>
</template>
